import React, { useRef, useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import CollaborativeTradingViewChart, {
  CollaborativeTradingViewChartRef,
} from '@/components/charts/CollaborativeTradingViewChart';
import { ChartSyncService, DrawingData } from '@/services/charts/chartSyncService';
import { Colors } from '@/constants/colors';
import { useAuthStore } from '@/store/authStore';
import { polygonChartDataService } from '@/services/api/polygonChartData.service';
import { ChartTimeframe, TIMEFRAME_OPTIONS, DEFAULT_TIMEFRAME } from '@/constants/chartTimeframes';
import { StockChartData } from '@/types/stock.types';

interface CollaborativeChartScreenProps {
  route: {
    params: {
      groupId: string;
      symbol: string;
      groupName?: string;
    };
  };
  navigation: any;
}

const CollaborativeChartScreen: React.FC<CollaborativeChartScreenProps> = ({
  route,
  navigation,
}) => {
  const { groupId, symbol, groupName } = route.params;
  const { user } = useAuthStore();
  const chartRef = useRef<CollaborativeTradingViewChartRef>(null);
  const [syncService, setSyncService] = useState<ChartSyncService | null>(null);
  const [isLoadingDrawings, setIsLoadingDrawings] = useState(true);
  const [syncStatus, setSyncStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [activeUsers] = useState(1); // Simplified for now
  const [isPrivateMode, setIsPrivateMode] = useState(false);
  const [timeframe, setTimeframe] = useState<ChartTimeframe>(DEFAULT_TIMEFRAME);
  const [chartData, setChartData] = useState<StockChartData[]>([]);
  const [isLoadingChart, setIsLoadingChart] = useState(true);
  const [chartError, setChartError] = useState<string | null>(null);
  const [activeSymbol, setActiveSymbol] = useState(symbol);
  const [isChartReady, setIsChartReady] = useState(false);

  // Reset readiness when symbol changes so effects wait for next ready
  useEffect(() => {
    setIsChartReady(false);
  }, [activeSymbol]);

  // Load chart data when symbol or timeframe changes (cancellable)
  useEffect(() => {
    const abort = new AbortController();
    const loadChartData = async () => {
      setIsLoadingChart(true);
      setChartError(null);

      console.log(`[ChartScreen] Loading ${timeframe} data for ${activeSymbol}`);

      const { data, error } = await polygonChartDataService.getChartData(activeSymbol, timeframe);

      if (abort.signal.aborted) return;

      if (error) {
        console.error('[ChartScreen] Failed to load chart data:', error);
        setChartError(error);
        setChartData([]);
      } else {
        console.log(`[ChartScreen] Loaded ${data.length} data points`);
        setChartData(data);
      }

      setIsLoadingChart(false);
    };

    loadChartData();
    return () => abort.abort();
  }, [activeSymbol, timeframe]);

  useEffect(() => {
    if (!user) {
      console.error('No user found, cannot initialize chart');
      return;
    }

    console.log('🎨 [ChartScreen] Initializing collaborative chart for', activeSymbol);

    // Initialize sync service
    const service = new ChartSyncService(groupId, user.id, activeSymbol, {
      onDrawingCreate: (drawing: DrawingData) => {
        console.log('📥 [ChartScreen] Received remote drawing create');
        chartRef.current?.addRemoteDrawing(drawing);
      },
      onDrawingUpdate: (drawing: DrawingData) => {
        console.log('📥 [ChartScreen] Received remote drawing update');
        chartRef.current?.updateRemoteDrawing(drawing);
      },
      onDrawingDelete: (drawingId: string) => {
        console.log('📥 [ChartScreen] Received remote drawing delete');
        chartRef.current?.removeRemoteDrawing(drawingId);
      },
      onError: (error: string) => {
        console.error('❌ [ChartScreen] Sync error:', error);
        setSyncStatus('error');
      },
    });

    // Subscribe to real-time updates
    service.subscribe();
    setSyncService(service);
    setSyncStatus('connected');

    // Load existing drawings
    setIsLoadingDrawings(true);
    service
      .loadDrawings(isPrivateMode)
      .then(drawings => {
        console.log(`[ChartScreen] Loaded ${drawings.length} existing drawings`);
        drawings.forEach(drawing => {
          chartRef.current?.addRemoteDrawing(drawing);
        });
      })
      .catch(error => {
        console.error('❌ [ChartScreen] Failed to load drawings:', error);
      })
      .finally(() => {
        setIsLoadingDrawings(false);
      });

    // Cleanup on unmount
    return () => {
      console.log('🔌 [ChartScreen] Cleaning up chart sync service');
      service.unsubscribe();
    };
  }, [groupId, activeSymbol, user, isPrivateMode]);

  // Reload drawings when private mode changes (only after chart ready and data loaded)
  useEffect(() => {
    if (!syncService || !user || !isChartReady || isLoadingChart) return;

    console.log(`🔄 [ChartScreen] Mode changed to ${isPrivateMode ? 'Private' : 'Collaborative'}`);

    // Clear existing drawings
    chartRef.current?.clearAllDrawings();

    // Reload with new filter
    setIsLoadingDrawings(true);
    syncService
      .loadDrawings(isPrivateMode)
      .then(drawings => {
        console.log(
          `[ChartScreen] Reloaded ${drawings.length} drawings in ${isPrivateMode ? 'private' : 'collaborative'} mode`
        );
        drawings.forEach(drawing => {
          chartRef.current?.addRemoteDrawing(drawing);
        });
      })
      .catch(error => {
        console.error('❌ [ChartScreen] Failed to reload drawings:', error);
      })
      .finally(() => {
        setIsLoadingDrawings(false);
      });
  }, [isPrivateMode, syncService, user, isChartReady, isLoadingChart]);

  const handleDrawingCreate = (drawing: DrawingData) => {
    if (!isPrivateMode) {
      console.log('[ChartScreen] Broadcasting drawing create');
      syncService?.broadcastDrawingCreate(drawing);
    } else {
      console.log('[ChartScreen] Private mode - saving without broadcast');
      syncService?.saveDrawingOnly(drawing);
    }
  };

  const handleDrawingUpdate = (drawing: DrawingData) => {
    if (!isPrivateMode) {
      console.log('[ChartScreen] Broadcasting drawing update');
      syncService?.broadcastDrawingUpdate(drawing);
    } else {
      console.log('[ChartScreen] Private mode - updating without broadcast');
      syncService?.updateDrawingOnly(drawing);
    }
  };

  const handleDrawingDelete = (drawingId: string) => {
    if (!isPrivateMode) {
      console.log('[ChartScreen] Broadcasting drawing delete');
      syncService?.broadcastDrawingDelete(drawingId);
    } else {
      console.log('[ChartScreen] Private mode - deleting without broadcast');
      syncService?.deleteDrawingOnly(drawingId);
    }
  };

  const handleChartReady = () => {
    console.log('[ChartScreen] Chart is ready');
    setIsChartReady(true);
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={Colors.background.primary} />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={Colors.text.primary} />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          <Text style={styles.symbolText}>{activeSymbol}</Text>
          {groupName && <Text style={styles.groupText}>{groupName}</Text>}
        </View>

        <View style={styles.headerRight}>
          {/* Private/Collaborative Mode Toggle */}
          <TouchableOpacity
            style={[styles.modeToggle, isPrivateMode && styles.modeTogglePrivate]}
            onPress={() => setIsPrivateMode(!isPrivateMode)}
            activeOpacity={0.7}
          >
            <Ionicons
              name={isPrivateMode ? 'lock-closed' : 'people'}
              size={14}
              color={isPrivateMode ? Colors.status.warning : Colors.accent.primary}
            />
            <Text style={[styles.modeToggleText, isPrivateMode && styles.modeToggleTextPrivate]}>
              {isPrivateMode ? 'Private' : 'Collaborative'}
            </Text>
          </TouchableOpacity>

          {/* Sync Status Indicator */}
          <View
            style={[
              styles.syncIndicator,
              syncStatus === 'connected' && styles.syncIndicatorConnected,
              syncStatus === 'error' && styles.syncIndicatorError,
            ]}
          >
            <View style={styles.syncDot} />
            <Text style={styles.syncText}>
              {syncStatus === 'connecting' && 'Connecting...'}
              {syncStatus === 'connected' && 'Live'}
              {syncStatus === 'error' && 'Error'}
            </Text>
          </View>

          {/* Active Users Count */}
          <View style={styles.usersCount}>
            <Ionicons name="people" size={16} color={Colors.accent.primary} />
            <Text style={styles.usersCountText}>{activeUsers}</Text>
          </View>
        </View>
      </View>

      {/* Timeframe Selector */}
      <View style={styles.timeframeContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.timeframeScroll}
        >
          {TIMEFRAME_OPTIONS.map(tf => (
            <TouchableOpacity
              key={tf}
              style={[styles.timeframeButton, timeframe === tf && styles.timeframeButtonActive]}
              onPress={() => setTimeframe(tf)}
              activeOpacity={0.7}
            >
              <Text style={[styles.timeframeText, timeframe === tf && styles.timeframeTextActive]}>
                {tf}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Loading Overlay */}
      {isLoadingDrawings && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingBox}>
            <ActivityIndicator size="small" color={Colors.accent.primary} />
            <Text style={styles.loadingText}>Loading drawings...</Text>
          </View>
        </View>
      )}

      <CollaborativeTradingViewChart
        ref={chartRef}
        symbol={activeSymbol}
        groupId={groupId}
        userId={user?.id || ''}
        userName={user?.name || 'Unknown'}
        chartData={chartData}
        isLoading={isLoadingChart}
        onReady={handleChartReady}
        onDrawingCreate={handleDrawingCreate}
        onDrawingUpdate={handleDrawingUpdate}
        onDrawingDelete={handleDrawingDelete}
        onRequestChangeSymbol={sym => {
          if (sym && typeof sym === 'string') {
            chartRef.current?.changeSymbol(sym);
            setActiveSymbol(sym);
          }
        }}
      />

      {chartError && (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={32} color={Colors.status.error} />
          <Text style={styles.errorText}>{chartError}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => setTimeframe(timeframe)}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.default,
  },
  backButton: {
    padding: 4,
  },
  headerCenter: {
    flex: 1,
    marginLeft: 12,
  },
  symbolText: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  groupText: {
    fontSize: 12,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  modeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    backgroundColor: Colors.accent.primary + '15',
    borderWidth: 1,
    borderColor: Colors.accent.primary,
    gap: 6,
  },
  modeTogglePrivate: {
    backgroundColor: Colors.status.warning + '15',
    borderColor: Colors.status.warning,
  },
  modeToggleText: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  modeToggleTextPrivate: {
    color: Colors.status.warning,
  },
  syncIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.default,
    gap: 6,
  },
  syncIndicatorConnected: {
    backgroundColor: Colors.status.success + '15',
    borderColor: Colors.status.success,
  },
  syncIndicatorError: {
    backgroundColor: Colors.status.error + '15',
    borderColor: Colors.status.error,
  },
  syncDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.text.secondary,
  },
  syncText: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  usersCount: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 12,
    backgroundColor: Colors.accent.primary + '15',
  },
  usersCountText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999,
  },
  loadingBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Colors.background.secondary,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.default,
  },
  loadingText: {
    fontSize: 14,
    color: Colors.text.primary,
  },
  timeframeContainer: {
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.default,
    paddingVertical: 8,
  },
  timeframeScroll: {
    paddingHorizontal: 16,
    gap: 8,
  },
  timeframeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.default,
  },
  timeframeButtonActive: {
    backgroundColor: Colors.accent.primary + '20',
    borderColor: Colors.accent.primary,
  },
  timeframeText: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  timeframeTextActive: {
    color: Colors.accent.primary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: Colors.background.primary,
    gap: 16,
  },
  errorText: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  retryButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: Colors.accent.primary,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },
});

export default CollaborativeChartScreen;
